import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const STATUS_DOT_COLORS = {
  // Appointment statuses
  scheduled: 'bg-blue-500',
  confirmed: 'bg-blue-800',
  completed: 'bg-green-600',
  cancelled: 'bg-gray-400',
  in_progress: 'bg-yellow-500',
  rescheduled: 'bg-purple-500',
  no_show: 'bg-gray-400',
  // Job statuses
  active: 'bg-blue-500',
  on_hold: 'bg-amber-500',
};

const STATUS_OPTIONS_DEFAULT = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const JOB_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const StatusSelect = ({ value, onChange, options = STATUS_OPTIONS_DEFAULT, size = 'sm', className = '', dropUp = false }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);

  const selected = options.find(o => o.value === value) || options[0];
  const dotColor = STATUS_DOT_COLORS[value] || 'bg-gray-300';

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = options.length * 36 + 8; // approximate
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = dropUp || spaceBelow < menuHeight && spaceAbove > menuHeight;
      setMenuStyle({
        position: 'fixed',
        right: window.innerWidth - rect.right,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        zIndex: 9999,
      });
    }
    setOpen(!open);
  };

  const textSize = size === 'lg' ? 'text-sm' : 'text-xs';
  const dotSize = size === 'lg' ? 'w-2.5 h-2.5' : 'w-2 h-2';
  const py = size === 'lg' ? 'py-1.5 px-2.5' : 'py-1 px-2';

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-1.5 ${py} rounded-full bg-gray-50 ${textSize} font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors`}
      >
        <span className={`${dotSize} rounded-full ${dotColor} flex-shrink-0`}></span>
        <span>{selected.label}</span>
        <ChevronDown size={size === 'lg' ? 14 : 12} className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div style={menuStyle} className="bg-white border border-gray-200 rounded-xl shadow-lg min-w-[140px] py-1">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 ${textSize} font-medium hover:bg-gray-50 transition-colors ${
                opt.value === value ? 'bg-gray-50 text-gray-900' : 'text-gray-600'
              }`}
            >
              <span className={`${dotSize} rounded-full ${STATUS_DOT_COLORS[opt.value] || 'bg-gray-300'} flex-shrink-0`}></span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusSelect;
export { STATUS_DOT_COLORS, JOB_STATUS_OPTIONS };
